/**
 * SlashInput
 *
 * A wrapper around a plain <input> that renders ghost-text autocomplete
 * behind the user's typed text. The ghost text is shown in a muted color
 * and can be accepted by pressing Tab.
 *
 * Architecture:
 *   <div relative>
 *     <span aria-hidden (ghost text, pointer-events-none, absolute)>
 *     <input (transparent bg, positioned on top)>
 *   </div>
 */

import { useRef, useEffect, forwardRef } from 'react';
import { cn } from '@/lib/utils';

interface SlashInputProps {
  value: string;
  onChange: (value: string) => void;
  onTabComplete: () => void;
  onEnter: () => void;
  onEscape: () => void;
  onArrowUp: () => void;
  onArrowDown: () => void;
  /** The full ghost text to show (includes what the user has already typed + the suffix) */
  ghostText: string;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}

export const SlashInput = forwardRef<HTMLInputElement, SlashInputProps>(
  function SlashInput(
    {
      value,
      onChange,
      onTabComplete,
      onEnter,
      onEscape,
      onArrowUp,
      onArrowDown,
      ghostText,
      placeholder,
      className,
      autoFocus,
    },
    ref,
  ) {
    const measureRef = useRef<HTMLSpanElement>(null);

    // Auto-focus when mounted
    useEffect(() => {
      if (autoFocus && ref && 'current' in ref) {
        ref.current?.focus();
      }
    }, [autoFocus, ref]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Tab') {
        e.preventDefault();
        onTabComplete();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        onEnter();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onEscape();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        onArrowUp();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        onArrowDown();
      }
    };

    // Only show ghost text if it starts with the current value
    const showGhost = ghostText.length > 0 && ghostText.startsWith(value) && ghostText !== value;
    // The ghost suffix is only the part the user hasn't typed
    const ghostSuffix = showGhost ? ghostText.slice(value.length) : '';

    return (
      <div className={cn('relative flex items-center', className)}>
        {/* Ghost text layer (rendered behind the input) */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 flex items-center px-3"
        >
          {/* Hidden span to measure typed text width — same font as input */}
          <span
            ref={measureRef}
            className="invisible whitespace-pre font-mono text-sm"
          >
            {value}
          </span>
          {ghostSuffix && (
            <span className="whitespace-pre font-mono text-sm text-muted-foreground/40 select-none">
              {ghostSuffix}
            </span>
          )}
        </div>

        {/* Actual input */}
        <input
          ref={ref}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          spellCheck={false}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          className={cn(
            'w-full bg-transparent px-3 py-2 font-mono text-sm outline-none placeholder:text-muted-foreground/50',
            'relative z-10',
          )}
        />
      </div>
    );
  },
);
